import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, unref as _unref, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "error-message"
}
const _hoisted_2 = {
  key: 1,
  class: "error-message"
}
const _hoisted_3 = { class: "field" }

import { BusinessHoursViewModel } from '@/features/bots/models';
import { BusinessHoursOption, BusinessHoursOptions, DayRange } from '.';
import { helpers } from '@vuelidate/validators';
import { HolidayViewModel } from '@/features/bots/models/holidayViewModel';
import { isSameDay, hasOverlappingOpenClose, hasOverlappingHours, hasAtLeastOneOnlineTime } from './validationHelpers';
import { computed, onMounted, PropType, ref, watch } from 'vue';
import { useStore } from 'vuex';
import useVuelidate from '@vuelidate/core';
import { useVModel } from '@vueuse/core';
import BusinessHoursRow from './BusinessHoursRow.vue';

const timezone = 'Europe/London';
const overlapErrorMessage = 'Must not contain conflicting business hours for the same day';
const atLeastOneOnlineTimeErrorMessage = 'Default Business Hours must be online for at least one day of the week';


export default /*@__PURE__*/_defineComponent({
  __name: 'BusinessHoursSelection',
  props: {
  modelValue: { type: Array as PropType<BusinessHoursViewModel[]>, required: true },
  allowEmpty: { type: Boolean, default: false },
  enforceOnlineDayOfWeek: { type: Boolean, default: false },
},
  emits: ["update:modelValue", "error"],
  setup(__props, { emit: __emit }) {

const hasNoOverlap = () =>
  helpers.withParams({ type: 'hasNoOverlap' }, (value: BusinessHoursOption[]) => {
    for (let i = 0; i < value.length; i++) {
      for (let j = i + 1; j < value.length; j++) {
        const current = value[i];
        const comparison = value[j];
        if (!isSameDay(current, comparison)) continue;
        if (hasOverlappingOpenClose(current, comparison) || hasOverlappingHours(current, comparison)) {
          return false;
        }
      }
    }
    return true;
  });

const hasOnlineTimes = () =>
  helpers.withParams(
    { type: 'hasOnlineTimes' },
    (value: BusinessHoursOption[], parentVm: any) => !parentVm.enforceOnlineDayOfWeek || hasAtLeastOneOnlineTime(value),
  );

// Props and Emits
const props = __props;

const emit = __emit;

const value = useVModel(props, 'modelValue', emit);
const openingTimes = ref<BusinessHoursOption[]>([]);

const rules = {
  openingTimes: {
    hasAtLeastOneOnlineTime: hasOnlineTimes(),
    hasNoOverlap: hasNoOverlap(),
  },
};

const $v = useVuelidate(rules, { openingTimes });

// Store and computed properties
const store = useStore();
const holidays = computed<HolidayViewModel[]>(() => store.getters.holidays);

const options = computed<DayRange[]>(() => {
  const opts = [
    { name: 'Select...', value: null, disabled: true },
    ...BusinessHoursOptions.dayOptions,
    { name: '──────────', value: 'divider-days', disabled: true },
    ...BusinessHoursOptions.dayRangeOptions,
  ];
  if (holidays.value.length > 0) {
    opts.push(
      { name: '──────────', value: 'divider-holidays', disabled: true },
      ...holidays.value.map((h: HolidayViewModel) => ({ name: h.name, holidayId: h.id })),
    );
  }
  return opts;
});

const showDeleteIcon = computed(() => openingTimes.value.length > 1 || props.allowEmpty);

// Lifecycle and methods
onMounted(() => {
  openingTimes.value = value.value.map((vm: BusinessHoursViewModel) => BusinessHoursOption.fromViewModel(vm));
  $v.value.$touch(); // Ensure validation state is initialized
});

function addOfficeHours() {
  openingTimes.value.push(BusinessHoursOptions.getDefaultOption());
  onChange();
}

function deleteOfficeHours(index: number) {
  openingTimes.value.splice(index, 1);
  onChange();
}

function onChange() {
  $v.value.$touch();
  emit('error', $v.value.$invalid);
  value.value = openingTimes.value.map((r: BusinessHoursOption) => r.toViewModel());
}

return (_ctx: any,_cache: any) => {
  const _component_n_button = _resolveComponent("n-button")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createElementVNode("div", { class: "timezone" }, [
      _cache[1] || (_cache[1] = _createElementVNode("span", { class: "timezone-label" }, "Timezone:", -1)),
      _createElementVNode("span", { class: "timezone-text" }, _toDisplayString(timezone))
    ]),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(openingTimes.value, (businessHoursRow, index) => {
      return (_openBlock(), _createBlock(BusinessHoursRow, {
        key: index,
        modelValue: openingTimes.value[index],
        "onUpdate:modelValue": [($event: any) => ((openingTimes.value[index]) = $event), onChange],
        options: options.value,
        "show-delete-icon": showDeleteIcon.value,
        holidays: holidays.value,
        onDelete: ($event: any) => (deleteOfficeHours(index))
      }, null, 8, ["modelValue", "onUpdate:modelValue", "options", "show-delete-icon", "holidays", "onDelete"]))
    }), 128)),
    (_unref($v).openingTimes.hasNoOverlap.$invalid)
      ? (_openBlock(), _createElementBlock("div", _hoisted_1, _toDisplayString(overlapErrorMessage)))
      : _createCommentVNode("", true),
    (_unref($v).openingTimes.hasAtLeastOneOnlineTime.$invalid)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(atLeastOneOnlineTimeErrorMessage)))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_component_n_button, {
        onClick: _cache[0] || (_cache[0] = ($event: any) => (addOfficeHours())),
        variant: "ghost",
        icon: "add"
      }, {
        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
          _createTextVNode("Add Times", -1)
        ]))]),
        _: 1
      })
    ])
  ]))
}
}

})